import "./company.css";
import SEO from "../../SEO";
import Icon from "../../components/Icon";
import useReveal from "../../hooks/useReveal";

/* ── Data ──────────────────────────────────────────────────────────────────── */
const stats = [
  { num: "14", label: "Projects Delivered" },
  { num: "2+", label: "Years of Expertise" },
  { num: "35", label: "Happy Clients" },
];

const missionItems = [
  "Deliver innovative AI and software solutions",
  "Simplify complex business problems",
  "Enable digital transformation for all industries",
];

const visionItems = [
  "Become a leader in AI-driven product development",
  "Build scalable solutions used globally",
  "Empower businesses with intelligent systems",
];

const services = [
  {
    icon: "layers",
    title: "AI Application Development",
    desc: "Custom AI systems — from intelligent chatbots and RAG pipelines to autonomous agents — built for real-world business scale.",
  },
  {
    icon: "monitor",
    title: "Web & Mobile App Development",
    desc: "Full-stack web and cross-platform mobile applications engineered for performance, usability, and long-term maintainability.",
  },
  {
    icon: "mobile",
    title: "SaaS Product Development",
    desc: "End-to-end SaaS platform engineering — from multi-tenant architecture and subscription billing to white-label delivery.",
  },
  {
    icon: "cloud",
    title: "Cloud & DevOps Solutions",
    desc: "Cloud-native infrastructure, CI/CD pipelines, containerisation, and observability — so your product ships fast and stays up.",
  },
  {
    icon: "workflow",
    title: "Application Modernization",
    desc: "Transform legacy monoliths into modular, cloud-ready microservices — preserving business logic while eliminating technical debt.",
  },
];

const strengths = [
  {
    num: "01",
    title: "AI + Full Stack Expertise",
    desc: "Our team bridges the gap between cutting-edge AI research and production-grade full-stack engineering — under one roof.",
  },
  {
    num: "02",
    title: "Scalable Architecture",
    desc: "Every system we build is designed to scale — from day-one prototypes to enterprise platforms handling millions of requests.",
  },
  {
    num: "03",
    title: "Fast Delivery, High Quality",
    desc: "We move with startup speed while maintaining the code quality, testing discipline, and documentation standards of mature engineering teams.",
  },
  {
    num: "04",
    title: "Client-Focused Approach",
    desc: "We treat every engagement as a true partnership — deep domain understanding, transparent communication, and outcomes over outputs.",
  },
];

/* ── Component ─────────────────────────────────────────────────────────────── */
export default function Company() {
  const [whoRef, whoVisible] = useReveal();
  const [mvRef, mvVisible] = useReveal();
  const [servicesRef, servicesVisible] = useReveal();
  const [strengthsRef, strengthsVisible] = useReveal();

  return (
    <>
      <SEO
        title="About Us: AI-First Product Engineering | Best Code Technology"
        description="An AI-first product engineering company building LLM-powered automation, custom AI systems, and production SaaS platforms."
        keywords="AI product company, AI engineering team, software development company, about us"
        url="https://bestcodetechnology.in/about/company"
      />
      <div className="co-page">
        {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
        <section className="co-hero">
          <div className="co-hero__orb" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <div className="co-hero__eyebrow">
                  <span className="co-tag co-tag--dark">
                    About Best Code Technology
                  </span>
                </div>

                <h1 className="co-hero__headline">
                  We Build Software That
                  <br />
                  <span>Thinks &amp; Transforms</span>
                </h1>

                <p className="co-hero__desc">
                  A team of passionate developers, AI engineers, and innovators
                  committed to delivering high-quality software solutions — from
                  startups to enterprises, we turn ideas into powerful digital
                  products.
                </p>
              </div>
            </div>

            {/* stat bar */}
            <div className="row justify-content-center mt-5">
              <div className="col-12 col-sm-10 col-md-8 col-lg-6">
                <div className="co-hero__stats">
                  {stats.map((s) => (
                    <div key={s.label} className="co-hero__stat">
                      <span className="co-hero__stat-num">{s.num}</span>
                      <span className="co-hero__stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          WHO WE ARE
      ══════════════════════════════════════════ */}
        <section className="co-who">
          <div className="container-xl">
            <div className="row align-items-center g-5" ref={whoRef}>
              {/* left — text */}
              <div
                className={`col-12 col-lg-6 reveal reveal--left ${
                  whoVisible ? "" : "is-hidden"
                }`}
              >
                <div className="co-who__tag">
                  <span className="co-tag co-tag--light">Who We Are</span>
                </div>
                <h2 className="co-who__heading">
                  An AI-First Product
                  <br />
                  Engineering Company
                </h2>
                <p className="co-who__body">
                  Best Code Technology is a Mahabubnagar-based software company
                  that lives at the intersection of artificial intelligence and
                  full-stack engineering. We partner with founders, product
                  teams, and enterprises to architect, build, and scale
                  intelligent digital products.
                </p>
                <p className="co-who__body">
                  From day-one MVPs to production platforms serving thousands of
                  users, we bring the same rigour, creativity, and commitment to
                  every engagement we take on.
                </p>

                <div className="co-who__highlight">
                  <div className="co-who__highlight-icon" aria-hidden="true">
                    <Icon name="layers" size={22} />
                  </div>
                  <div>
                    <p className="co-who__highlight-text">
                      AI-first. Production-ready. Client-focused.
                    </p>
                    <p className="co-who__highlight-sub">
                      Our tagline says it all — Reach Heights with Tech.
                    </p>
                  </div>
                </div>
              </div>

              {/* right — dark visual card */}
              <div
                className={`col-12 col-lg-6 reveal reveal--right ${
                  whoVisible ? "" : "is-hidden"
                }`}
              >
                <div className="co-who__visual">
                  <div className="co-who__visual-card">
                    <div>
                      <p className="co-who__vc-label">
                        Mahabubnagar, Telangana · India
                      </p>
                      <h3 className="co-who__vc-headline">
                        Building the future,
                        <br />
                        one intelligent product at a time
                      </h3>
                    </div>

                    <ul className="co-who__vc-items">
                      <li className="co-who__vc-item">
                        AI Application Development
                      </li>
                      <li className="co-who__vc-item">
                        Full Stack Web &amp; Mobile
                      </li>
                      <li className="co-who__vc-item">
                        SaaS &amp; Cloud Platforms
                      </li>
                      <li className="co-who__vc-item">
                        DevOps &amp; Application Modernization
                      </li>
                    </ul>

                    <p className="co-who__vc-footer">
                      "We don't just write code — we engineer growth."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          MISSION & VISION
      ══════════════════════════════════════════ */}
        <section className="co-mv">
          <div className="container-xl">
            {/* header */}
            <div className="row justify-content-center co-mv__header">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <p className="co-mv__pretitle">Our Purpose</p>
                <h2 className="co-mv__heading">Mission &amp; Vision</h2>
                <p className="co-mv__sub">
                  Two commitments that guide every line of code we write and
                  every partnership we form.
                </p>
              </div>
            </div>

            {/* cards */}
            <div className="row g-4 reveal-stagger" ref={mvRef}>
              {/* mission */}
              <div
                className={`col-12 col-lg-6 reveal ${
                  mvVisible ? "" : "is-hidden"
                }`}
                style={{ "--stagger-index": 0 }}
              >
                <div className="co-mv-card co-mv-card--mission h-100">
                  <div className="co-mv-card__icon-wrap">
                    <Icon name="target" size={24} />
                  </div>
                  <div>
                    <p className="co-mv-card__label">Our Mission</p>
                    <h3 className="co-mv-card__title">What We're Here to Do</h3>
                  </div>
                  <ul className="co-mv-card__list">
                    {missionItems.map((item) => (
                      <li key={item} className="co-mv-card__item">
                        <span
                          className="co-mv-card__item-dot"
                          aria-hidden="true"
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* vision */}
              <div
                className={`col-12 col-lg-6 reveal ${
                  mvVisible ? "" : "is-hidden"
                }`}
                style={{ "--stagger-index": 1 }}
              >
                <div className="co-mv-card co-mv-card--vision h-100">
                  <div className="co-mv-card__icon-wrap">
                    <Icon name="trending" size={24} />
                  </div>
                  <div>
                    <p className="co-mv-card__label">Our Vision</p>
                    <h3 className="co-mv-card__title">Where We're Going</h3>
                  </div>
                  <ul className="co-mv-card__list">
                    {visionItems.map((item) => (
                      <li key={item} className="co-mv-card__item">
                        <span
                          className="co-mv-card__item-dot"
                          aria-hidden="true"
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          WHAT WE DO
      ══════════════════════════════════════════ */}
        <section className="co-services">
          <div className="container-xl">
            <div className="row justify-content-center co-services__header">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="co-tag co-tag--light mb-4 d-inline-flex">
                  What We Do
                </span>
                <h2 className="svc-section-title">Five Core Service Lines</h2>
                <p className="svc-section-sub">
                  From AI engineering to cloud infrastructure — everything you
                  need to build, launch, and scale a modern digital product.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={servicesRef}>
              {services.map((svc, i) => (
                <div
                  key={svc.title}
                  className={`col-12 col-md-6 col-xl-4 reveal ${
                    servicesVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="co-svc-card">
                    <div className="co-svc-card__icon">
                      <Icon name={svc.icon} size={24} />
                    </div>
                    <div className="co-svc-card__line" aria-hidden="true" />
                    <h3 className="co-svc-card__title">{svc.title}</h3>
                    <p className="co-svc-card__desc">{svc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          OUR STRENGTHS
      ══════════════════════════════════════════ */}
        <section className="co-strengths">
          <div className="container-xl">
            <div className="row justify-content-center co-strengths__header">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="co-tag co-tag--light mb-4 d-inline-flex">
                  Our Strengths
                </span>
                <h2 className="svc-section-title">Why Teams Choose Us</h2>
                <p className="svc-section-sub">
                  Four pillars that define how we work and why our clients come
                  back to build the next product with us.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={strengthsRef}>
              {strengths.map((s, i) => (
                <div
                  key={s.num}
                  className={`col-12 col-md-6 reveal ${
                    strengthsVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="co-strength-item">
                    <div className="co-strength-item__num" aria-hidden="true">
                      <span>{s.num}</span>
                    </div>
                    <div className="co-strength-item__body">
                      <h3 className="co-strength-item__title">{s.title}</h3>
                      <p className="co-strength-item__desc">{s.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
